import React, { useEffect, useState } from 'react';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { TWInput, ToggleSwitch, FieldLabel } from '../../../components/ui';
import { cn } from '../../../components/ui/_cn';
import {
  ORDER_NOTIFICATION_CODES,
  isValidNotificationEmail,
} from '../order-notification-settings';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All orders' },
  { value: 'asap', label: 'ASAP only' },
  { value: 'scheduled', label: 'Scheduled only' },
];

const OrderNotificationRow = ({ settings, saving, onChange }) => {
  const [emailDraft, setEmailDraft] = useState(settings.email);
  // Which control started the in-flight save, so only the email button
  // reads "Saving…" when it was the one clicked.
  const [pendingKind, setPendingKind] = useState(null);

  useEffect(() => {
    setEmailDraft(settings.email);
  }, [settings.email]);

  useEffect(() => {
    if (!saving) setPendingKind(null);
  }, [saving]);

  const submit = (kind, changes) => {
    setPendingKind(kind);
    onChange(changes);
  };

  const draft = emailDraft.trim();
  const emailInvalid = draft.length > 0 && !isValidNotificationEmail(draft);
  const canSaveEmail =
    !saving && !emailInvalid && draft.length > 0 && draft !== settings.email;

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
            New order emails
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Email each paid online order (web &amp; mobile app) to your
            restaurant the moment it comes in.
          </p>
        </div>
        <ToggleSwitch
          checked={settings.enabled}
          disabled={saving}
          ariaLabel="New order emails"
          onChange={(next) =>
            submit('enabled', {
              [ORDER_NOTIFICATION_CODES.ENABLED]: String(next),
            })
          }
        />
      </div>

      {settings.enabled && (
        <div className="mt-4 rounded-md border border-slate-200 bg-slate-50/60 p-4 dark:border-slate-700 dark:bg-slate-900/40">
          <form
            aria-label="Notification email"
            className="flex flex-col gap-3 sm:flex-row sm:items-start"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSaveEmail) {
                submit('email', { [ORDER_NOTIFICATION_CODES.EMAIL]: draft });
              }
            }}
          >
            <TWInput
              id="order-notification-email"
              label="Send to"
              type="email"
              leftIcon={EnvelopeIcon}
              placeholder="orders@yourrestaurant.com"
              value={emailDraft}
              disabled={saving}
              onChange={(e) => setEmailDraft(e.target.value)}
              error={emailInvalid ? 'Enter a valid email address.' : undefined}
              hint={
                !settings.email
                  ? 'Add an email address to start receiving order emails.'
                  : undefined
              }
            />
            <button
              type="submit"
              disabled={!canSaveEmail}
              className="inline-flex h-10 shrink-0 items-center justify-center rounded-md border-none bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-sky-500 dark:hover:bg-sky-600 sm:mt-[22px]"
            >
              {saving && pendingKind === 'email' ? 'Saving…' : 'Save email'}
            </button>
          </form>

          <div className="mt-4">
            <FieldLabel>Which orders</FieldLabel>
            <div
              role="radiogroup"
              aria-label="Which orders"
              className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3"
            >
              {FILTER_OPTIONS.map((opt) => {
                const active = opt.value === settings.filter;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={saving}
                    onClick={() => {
                      if (opt.value !== settings.filter) {
                        submit('filter', {
                          [ORDER_NOTIFICATION_CODES.FILTER]: opt.value,
                        });
                      }
                    }}
                    className={cn(
                      'flex h-10 items-center justify-center rounded-md border text-sm font-medium transition',
                      'disabled:cursor-not-allowed disabled:opacity-60',
                      active
                        ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-sky-400 dark:bg-sky-400/10 dark:text-sky-300'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700/60',
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderNotificationRow;
